import { useState, useMemo } from "react";
import { useBrand } from "../../../context/brandContext";
import { TenantType } from "../../../constants/enum";
import TenantGrid from "./TenantGrid";
import TenantServiceModal from "./TenantServiceModal";
import TenantServiceOptionsModal from "./TenantServiceOptionsModal";

type MaybeCategory = { id?: string; name?: string };

function TenantsList() {
  const [error] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedTenantType, setSelectedTenantType] = useState<string>("all");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const brand = useBrand();

  // Service modal state
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<{ id: string; name: string } | null>(null);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<{ tenantId: string; serviceId: string; serviceName: string } | null>(null);

  
   const normalizeName = (s?: string) =>
    (s || "").toString().trim().toLowerCase();
    const uniqueCategories = useMemo(() => {
    const map = new Map<
      string,
      { key: string; id?: string; name: string; ids: Set<string> }
    >();

    const tenants = brand.brand?.tenants || [];

    tenants.forEach((tenant: any) => {
      (tenant.categories || []).forEach((cat: MaybeCategory) => {
        const rawName = cat?.name;
        if (!rawName) return; // skip categories without a name
        const key = normalizeName(rawName);
        if (!key) return;

        const existing = map.get(key);
        if (existing) {
          if (cat?.id) existing.ids.add(cat.id);
          if (!existing.id && cat?.id) existing.id = cat.id; // keep an id if available
        } else {
          map.set(key, {
            key,
            id: cat?.id,
            name: rawName.trim(),
            ids: new Set(cat?.id ? [cat.id] : []),
          });
        }
      });
    });

    // Convert to array and sort alphabetically by display name
    return Array.from(map.values())
      .map(({ key, id, name }) => ({ key, id: id || key, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brand.brand?.tenants]);

  const filteredTenants = useMemo(() => {
    const tenants = brand.brand?.tenants || [];

    let filtered = tenants;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((tenant: any) =>
        (tenant.categories || []).some((cat: MaybeCategory) => {
          const nameKey = normalizeName(cat?.name);
          const id = cat?.id;
          return nameKey === selectedCategory || id === selectedCategory;
        })
      );
    }

    // Filter by tenant type
    if (selectedTenantType !== "all") {
      filtered = filtered.filter((tenant: any) => {
        const tenantType = tenant.tenantType || TenantType.NON_REAL_ESTATE;
        return tenantType === selectedTenantType;
      });
    }

    // Sort by creation date (newest first)
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [brand.brand?.tenants, selectedCategory, selectedTenantType]);

  const handleOpenServiceModal = (tenantId: string, tenantName: string) => {
    setSelectedTenant({ id: tenantId, name: tenantName });
    setServiceModalOpen(true);
  };
  const handleCloseServiceModal = () => {
    setServiceModalOpen(false);
    setSelectedTenant(null);
  };
  const handleCloseOptionsModal = () => {
    setOptionsModalOpen(false);
    setSelectedService(null);
  };

  if (brand.loading) {
    return (
      <div className="bg-[#FDFDFD] min-h-screen">
        <main className="px-6 py-10 max-w-7xl mx-auto">
          <h2
            style={{ fontFamily: '"Manrope", sans-serif' }}
            className="text-center text-3xl font-light"
          >
            Tenant Documents
          </h2>
          <div className="flex justify-center items-center mt-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
          <p className="text-center text-gray-500 mt-4">Loading tenants...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#FDFDFD] min-h-screen">
        <main className="px-6 py-10 max-w-7xl mx-auto">
          <h2
            style={{ fontFamily: '"Manrope", sans-serif' }}
            className="text-center text-3xl font-light"
          >
            Tenant Documents
          </h2>
          <div className="text-center mt-10">
            <div className="text-red-500 text-lg mb-4">
              Error loading tenants
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-[#FDFDFD] max-h-[90vh] overflow-x-auto">
      <main className="px-6 py-10 max-w-7xl mx-auto">
        <div className="flex justify-center items-center mb-6 font-light">
          {(filteredTenants?.length ?? 0).toLocaleString()} Reports
          {(selectedCategory !== "all" || selectedTenantType !== "all") && (
            <span className="ml-2 text-sm text-gray-500">
              (filtered)
            </span>
          )}
        </div>

        <h2 className="text-center text-slate-900 text-5xl font-light mb-10" style={{ fontFamily: '"Manrope", sans-serif' }}>
          Company Reports
        </h2>

        {/* Category Filter */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">Filter by Category</h3>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-1 rounded-full text-sm border cursor-pointer ${selectedCategory === "all" ? "bg-blue-50 text-blue-600 border-blue-200" : "text-gray-800 border-gray-200"}`}
            >
              All 
            </button>
            {(showAllCategories ? uniqueCategories : uniqueCategories.slice(0, 10)).map((category) => {
              const key = category.key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 font-manrope font-light capitalize rounded-full text-sm border cursor-pointer ${
                    selectedCategory === key ? "bg-blue-50 text-blue-600 border-blue-200" : "text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {category.name} 
                </button>
              );
            })}
            {uniqueCategories.length > 10 && (
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="px-4 py-2 font-manrope font-light rounded-full text-sm border cursor-pointer text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100"
              >
                {showAllCategories ? 'Show Less' : `Show More (${uniqueCategories.length - 10})`}
              </button>
            )}
          </div>
        </div>

        {/* Tenant Type Filter */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">Filter by Type</h3>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedTenantType("all")}
              className={`px-4 py-2 font-manrope font-light rounded-full text-sm border cursor-pointer ${
                selectedTenantType === "all" 
                  ? "bg-blue-50 text-blue-600 border-blue-200" 
                  : "text-gray-700 border-gray-200 hover:bg-gray-100"
              }`}
            >
              All Types
            </button>
            <button
              onClick={() => setSelectedTenantType(TenantType.NON_REAL_ESTATE)}
              className={`px-4 py-2 font-manrope font-light rounded-full text-sm border cursor-pointer ${
                selectedTenantType === TenantType.NON_REAL_ESTATE 
                  ? "bg-blue-50 text-blue-600 border-blue-200" 
                  : "text-gray-700 border-gray-200 hover:bg-gray-100"
              }`}
            >
              Non Real Estate
            </button>
            <button
              onClick={() => setSelectedTenantType(TenantType.REAL_ESTATE)}
              className={`px-4 py-2 font-manrope font-light rounded-full text-sm border cursor-pointer ${
                selectedTenantType === TenantType.REAL_ESTATE 
                  ? "bg-blue-50 text-blue-600 border-blue-200" 
                  : "text-gray-700 border-gray-200 hover:bg-gray-100"
              }`}
            >
              Real Estate
            </button>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(selectedCategory !== "all" || selectedTenantType !== "all") && (
          <div className="mb-6 text-center">
            <button
              onClick={() => {
                setSelectedCategory("all");
                setSelectedTenantType("all");
              }}
              className="px-4 py-2 font-manrope font-light text-sm text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Grid displaying filtered reports */}
        <TenantGrid tenants={filteredTenants} onServiceManagement={handleOpenServiceModal} />

        {/* Service Management Modal */}
        {selectedTenant && (
          <TenantServiceModal
            isOpen={serviceModalOpen}
            onClose={handleCloseServiceModal}
            tenantId={selectedTenant.id}
            tenantName={selectedTenant.name}
            onServiceUpdate={() => {
              console.log("Services updated for tenant:", selectedTenant.name);
            }}
          />
        )}

        {/* Service Options Modal */}
        {selectedService && (
          <TenantServiceOptionsModal
            isOpen={optionsModalOpen}
            onClose={handleCloseOptionsModal}
            tenantId={selectedService.tenantId}
            serviceId={selectedService.serviceId}
            serviceName={selectedService.serviceName}
            onOptionsUpdate={() => {
              console.log(
                "Options updated for service:",
                selectedService.serviceName
              );
            }}
          />
        )}
      </main>
    </div>
  );
}

export default TenantsList;
